import { BRAND_HEX } from '../../styles/brand-colors';

/**
 * Notification icon / dot sizing + colour resolvers. Mirrors `Toast.icons.ts`:
 * static glyphs resolve to a hex `color` on BOTH platforms (lucide-react-native
 * / react-native-svg ignore `className`), while the title/description/timestamp
 * keep their fixed text colours.
 */

/** Leading-icon px size (Figma: 22px at every content size, node 2013:10883). */
export const NOTIFICATION_LEADING_ICON_SIZE = 22;

/** Avatar box px (Figma 40px rounded-full). Drives the reserved leading slot. */
export const NOTIFICATION_AVATAR_SIZE = 40;

/** Unread dot diameter px (Figma 8px, brand-primary, node 1780:1869). */
export const NOTIFICATION_DOT_SIZE = 8;

/** Dismiss-X px (aligned with Toast sm). */
export const NOTIFICATION_DISMISS_ICON_SIZE = 18;

/**
 * Leading-icon colour per dark-mode. The leading glyph is a static icon
 * distinct from the title colour, so it resolves to hex (not `currentColor`).
 * Figma's compact-icon node renders a neutral glyph; default to secondary-600
 * (light) / secondary-400 (dark, palindromic swap). Callers can override via
 * {@link NotificationProps.iconColor}.
 */
const LEADING_COLOR = {
  light: BRAND_HEX.secondary['600'],
  dark: BRAND_HEX.secondary['400'],
} as const;

export function resolveNotificationIconColor(isDark: boolean): string {
  return isDark ? LEADING_COLOR.dark : LEADING_COLOR.light;
}

/** Dismiss-X colour — neutral, Toast-style (secondary-400 / secondary-500). */
const DISMISS_COLOR = {
  light: BRAND_HEX.secondary['400'],
  dark: BRAND_HEX.secondary['500'],
} as const;

export function resolveNotificationDismissColor(isDark: boolean): string {
  return isDark ? DISMISS_COLOR.dark : DISMISS_COLOR.light;
}

/**
 * Unread dot colour — brand-primary-500 (#8e51ff), a non-inverting mid shade.
 * Kept here as the single source; applied as the `bg-primary-500` class on the
 * dot `<View>` (a plain coloured box, no SVG — cross-platform-safe).
 */
export const NOTIFICATION_DOT_COLOR = BRAND_HEX.primary['500'];
